"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const spellCheckService_1 = require("../services/spellCheckService");
const SpellCheckHighlighter = ({ text, onChange, onKeyDown, className = '', placeholder = '', disabled = false, rows = 2 }) => {
    const [spellCheckResult, setSpellCheckResult] = (0, react_1.useState)(null);
    const [suggestionPopup, setSuggestionPopup] = (0, react_1.useState)(null);
    const textareaRef = (0, react_1.useRef)(null);
    const highlightRef = (0, react_1.useRef)(null);
    const containerRef = (0, react_1.useRef)(null);
    // Debounced spell check (with performance optimization)
    (0, react_1.useEffect)(() => {
        const timeoutId = setTimeout(async () => {
            if (text.trim()) {
                // Skip spell check for very long texts to prevent freezing
                if (text.length > 1000) {
                    console.log('Skipping spell check for long text (>1000 chars)');
                    setSpellCheckResult(null);
                    return;
                }
                try {
                    const result = await spellCheckService_1.spellCheckService.checkSpelling(text);
                    setSpellCheckResult(result);
                }
                catch (error) {
                    console.error('Spell check failed:', error);
                    setSpellCheckResult(null);
                }
            }
            else {
                setSpellCheckResult(null);
            }
        }, 500); // 500ms debounce pro lepší výkon
        return () => clearTimeout(timeoutId);
    }, [text]);
    // Sync scroll between textarea and highlight layer
    const handleScroll = () => {
        if (textareaRef.current && highlightRef.current) {
            highlightRef.current.scrollTop = textareaRef.current.scrollTop;
            highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
        }
    };
    // Handle text change
    const handleTextChange = (e) => {
        onChange(e.target.value);
        setSuggestionPopup(null); // Close any open suggestion popup
    };
    // Handle right-click on misspelled word (with lazy loading of suggestions)
    const handleContextMenu = async (e) => {
        e.preventDefault();
        if (!spellCheckResult || !textareaRef.current)
            return;
        const textarea = textareaRef.current;
        const cursorPosition = textarea.selectionStart;
        // Find if cursor is on a misspelled word
        const error = spellCheckResult.errors.find(err => cursorPosition >= err.position.start && cursorPosition <= err.position.end);
        if (error) {
            // Lazy load suggestions
            const suggestions = await spellCheckService_1.spellCheckService.getSuggestionsForWord(error.word);
            if (suggestions.length > 0) {
                setSuggestionPopup({
                    word: error.word,
                    suggestions: suggestions,
                    position: { x: e.clientX, y: e.clientY },
                    textPosition: error.position
                });
            }
            else {
                setSuggestionPopup(null);
            }
        }
        else {
            setSuggestionPopup(null);
        }
    };
    // Apply suggestion
    const applySuggestion = (suggestion) => {
        if (!suggestionPopup)
            return;
        const newText = text.substring(0, suggestionPopup.textPosition.start) +
            suggestion +
            text.substring(suggestionPopup.textPosition.end);
        onChange(newText);
        setSuggestionPopup(null);
        // Focus back to textarea
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                const newCursorPos = suggestionPopup.textPosition.start + suggestion.length;
                textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
            }
        }, 0);
    };
    // Add to dictionary
    const addToDictionary = () => {
        if (!suggestionPopup)
            return;
        spellCheckService_1.spellCheckService.addToPersonalDictionary(suggestionPopup.word);
        setSuggestionPopup(null);
        // Re-run spell check to update highlights
        setTimeout(async () => {
            if (text.trim()) {
                const result = await spellCheckService_1.spellCheckService.checkSpelling(text);
                setSpellCheckResult(result);
            }
        }, 100);
    };
    // Close suggestion popup when clicking outside
    (0, react_1.useEffect)(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setSuggestionPopup(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    // Generate highlighted text HTML
    const generateHighlightedText = () => {
        if (!spellCheckResult || spellCheckResult.errors.length === 0) {
            return text.replace(/\n/g, '<br>').replace(/ /g, '&nbsp;');
        }
        let highlightedText = text;
        let offset = 0;
        // Sort errors by position to avoid conflicts
        const sortedErrors = [...spellCheckResult.errors].sort((a, b) => a.position.start - b.position.start);
        for (const error of sortedErrors) {
            const start = error.position.start + offset;
            const end = error.position.end + offset;
            const word = highlightedText.substring(start, end);
            const highlightedWord = `<span class="spell-error" title="Možná chyba: ${error.word}">${word}</span>`;
            highlightedText = highlightedText.substring(0, start) +
                highlightedWord +
                highlightedText.substring(end);
            offset += highlightedWord.length - word.length;
        }
        // DŮLEŽITÉ: Nahradit nové řádky a mezery, ale zachovat HTML tagy
        // Nejprve nahradíme nové řádky
        highlightedText = highlightedText.replace(/\n/g, '<br>');
        // Pak nahradíme mezery za &nbsp;, ale pouze mimo HTML tagy
        // Použijeme regex, který nahradí mezery pouze mimo < a >
        highlightedText = highlightedText.replace(/ (?![^<]*>)/g, '&nbsp;');
        return highlightedText;
    };
    return ((0, jsx_runtime_1.jsxs)("div", { ref: containerRef, className: "spell-check-container", children: [(0, jsx_runtime_1.jsx)("div", { ref: highlightRef, className: `spell-check-highlight ${className}`, dangerouslySetInnerHTML: {
                    __html: generateHighlightedText()
                } }), (0, jsx_runtime_1.jsx)("textarea", { ref: textareaRef, value: text, onChange: handleTextChange, onKeyDown: onKeyDown, onScroll: handleScroll, onContextMenu: handleContextMenu, className: `spell-check-textarea ${className}`, placeholder: placeholder, disabled: disabled, rows: rows, spellCheck: false }), suggestionPopup && ((0, jsx_runtime_1.jsxs)("div", { className: "spell-suggestion-popup", style: {
                    left: suggestionPopup.position.x,
                    top: suggestionPopup.position.y + 10
                }, children: [(0, jsx_runtime_1.jsxs)("div", { className: "spell-suggestion-header", children: ["N\u00E1vrhy pro \"", suggestionPopup.word, "\""] }), suggestionPopup.suggestions.map((suggestion, index) => ((0, jsx_runtime_1.jsx)("button", { onClick: () => applySuggestion(suggestion), className: "spell-suggestion-item", children: suggestion }, index))), (0, jsx_runtime_1.jsx)("button", { onClick: addToDictionary, className: "spell-suggestion-add", children: "P\u0159idat do slovn\u00EDku" })] }))] }));
};
exports.default = SpellCheckHighlighter;
